import type { PostV2WorkspacesWorkspaceidFilesMutationRequest } from '@emma/api_client/types';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { downloadBase64File } from '@/hooks/file/file_download_helper';
import { useApiFetch, useApiMutation, useCallApi } from '../api';

export function useWorkspaceFile(workspaceId: number, fileId: number) {
  return useApiFetch(
    `workspaces/${workspaceId}/files/${fileId}`,
    async (callApi) =>
      await callApi((api) =>
        api.files.getV2WorkspacesWorkspaceidFilesFileid({ workspaceId, fileId })
      )
  );
}

export function useAddWorkspaceFile(workspaceId: number, actionOnErr: () => void = () => {}) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/files`;

  const addFileMutation = useApiMutation(
    listCacheKey,
    async (callApi, updates: PostV2WorkspacesWorkspaceidFilesMutationRequest) => {
      const response = await callApi((api) =>
        api.files.postV2WorkspacesWorkspaceidFiles({ workspaceId }, updates)
      );
      return response.data;
    },
    actionOnErr
  );

  return useCallback(
    async (updates: PostV2WorkspacesWorkspaceidFilesMutationRequest) => {
      const result = await addFileMutation(updates);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      return result;
    },
    [addFileMutation, mutate, listCacheKey]
  );
}

export function useDeleteWorkspaceFile(workspaceId: number, actionOnErr: () => void = () => {}) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/files`;

  const deleteFileMutation = useApiMutation(
    listCacheKey,
    async (callApi, fileId: number) => {
      await callApi((api) =>
        api.files.deleteV2WorkspacesWorkspaceidFilesFileid({
          workspaceId,
          fileId,
        })
      );
      return fileId;
    },
    actionOnErr
  );

  return useCallback(
    async (fileId: number) => {
      await deleteFileMutation(fileId);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      await mutate(`workspaces/${workspaceId}/files/${fileId}`);
      return true;
    },
    [deleteFileMutation, mutate, listCacheKey, workspaceId]
  );
}

export function useUpdateWorkspaceFile(workspaceId: number, actionOnErr: () => void = () => {}) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/files`;

  const updateFileMutation = useApiMutation(
    listCacheKey,
    async (callApi, { fileId, isDisabled }: { fileId: number; isDisabled: boolean }) => {
      const response = await callApi((api) =>
        api.files.putV2WorkspacesWorkspaceidFilesFileid({ workspaceId, fileId }, { isDisabled })
      );
      return response.data;
    },
    actionOnErr
  );

  return useCallback(
    async (fileId: number, isDisabled: boolean) => {
      const result = await updateFileMutation({ fileId, isDisabled });
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      await mutate(`workspaces/${workspaceId}/files/${fileId}`);
      return result;
    },
    [updateFileMutation, mutate, listCacheKey, workspaceId]
  );
}

export function useWorkspaceFileDownload(workspaceId: number, actionOnErr?: () => void) {
  const callApi = useCallApi();

  return useCallback(
    async (fileId: number, fileName: string) => {
      try {
        const response = await callApi((api) =>
          api.files.getV2WorkspacesWorkspaceidFilesFileid({
            workspaceId,
            fileId,
          })
        );

        if (
          !response.data?.fileContents ||
          !downloadBase64File(response.data.fileContents, fileName)
        ) {
          actionOnErr?.();
        }
      } catch {
        actionOnErr?.();
      }
    },
    [callApi, workspaceId, actionOnErr]
  );
}
